import AddRoundedIcon from '@mui/icons-material/AddRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded'
import PrintRoundedIcon from '@mui/icons-material/PrintRounded'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import {
  createQuickActionReceipt,
  fetchQuickActionReceiptPdf,
  sendQuickActionReceipt,
} from './patientQuickActionsService'

const INDIGO = '#4338ca'
const PAY_MODES = [
  { value: 'cash', label: 'Cash' },
  { value: 'phonepe', label: 'PhonePe' },
  { value: 'google_pay', label: 'Google Pay' },
  { value: 'paytm', label: 'Paytm' },
  { value: 'other_upi', label: 'Other UPI' },
  { value: 'debit_and_credit_cards', label: 'Card' },
]
const SERVICE_SUGGESTIONS = [
  'Consultation',
  'Follow-up consultation',
  'Procedure',
  'Therapy session',
  'Investigation',
]

const inr = (value) => `₹${(Number(value) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`
const num = (value) => (value === '' || value === null || value === undefined ? 0 : Number(value) || 0)

let rowSeq = 0
const emptyRow = () => ({ id: `row-${(rowSeq += 1)}`, description: '', qty: 1, amount: '', discount: '' })

function computeTotals(lineItems, additionalDiscount) {
  let totalAmount = 0
  let lineDiscount = 0
  lineItems.forEach((li) => {
    totalAmount += num(li.qty) * num(li.amount)
    lineDiscount += num(li.discount)
  })
  const addl = num(additionalDiscount)
  const totalDiscount = lineDiscount + addl
  const grandTotal = Math.max(0, totalAmount - totalDiscount)
  return { totalAmount, lineDiscount, additionalDiscount: addl, totalDiscount, grandTotal }
}

function paymentStatusOf(amountPaid, grandTotal) {
  const paid = num(amountPaid)
  if (grandTotal > 0 && paid >= grandTotal) return { label: 'Paid', color: '#16a34a' }
  if (paid > 0 && paid < grandTotal) return { label: 'Partial', color: '#d97706' }
  return { label: 'Unbilled', color: '#6b7280' }
}

export default function BillPatientDialog({ open, onClose, contactContext, patientName, onSent }) {
  const [mode, setMode] = useState('edit')
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [lineItems, setLineItems] = useState([emptyRow()])
  const [additionalDiscount, setAdditionalDiscount] = useState('')
  const [payMode, setPayMode] = useState('cash')
  const [amountPaid, setAmountPaid] = useState('')
  const [paymentId, setPaymentId] = useState('')
  const [remarks, setRemarks] = useState('')
  const [creating, setCreating] = useState(false)
  const [result, setResult] = useState(null)
  const [pdfUrl, setPdfUrl] = useState('')
  const [loadingPdf, setLoadingPdf] = useState(false)
  const [sending, setSending] = useState(false)
  const pdfUrlRef = useRef('')

  const totals = useMemo(() => computeTotals(lineItems, additionalDiscount), [lineItems, additionalDiscount])
  const status = paymentStatusOf(amountPaid, totals.grandTotal)
  const hasValidLine = lineItems.some((li) => li.description.trim() && num(li.qty) > 0 && num(li.amount) >= 0)

  const revokePdf = useCallback(() => {
    if (pdfUrlRef.current) {
      URL.revokeObjectURL(pdfUrlRef.current)
      pdfUrlRef.current = ''
    }
  }, [])

  const resetForm = useCallback(() => {
    setMode('edit')
    setName(patientName || contactContext?.patientName || '')
    setPhone(contactContext?.patientMobile || '')
    setLineItems([emptyRow()])
    setAdditionalDiscount('')
    setPayMode('cash')
    setAmountPaid('')
    setPaymentId('')
    setRemarks('')
    setResult(null)
    setLoadingPdf(false)
    setPdfUrl('')
    revokePdf()
  }, [patientName, contactContext, revokePdf])

  useEffect(() => {
    if (open) resetForm()
    return () => revokePdf()
  }, [open, resetForm, revokePdf])

  const updateRow = (id, field, value) =>
    setLineItems((rows) => rows.map((r) => (r.id === id ? { ...r, [field]: value } : r)))
  const removeRow = (id) => setLineItems((rows) => (rows.length > 1 ? rows.filter((r) => r.id !== id) : rows))
  const addRow = () => setLineItems((rows) => [...rows, emptyRow()])

  const loadPdf = useCallback(async (receiptId) => {
    setLoadingPdf(true)
    const res = await fetchQuickActionReceiptPdf(receiptId)
    setLoadingPdf(false)
    if (res?.ok) {
      revokePdf()
      pdfUrlRef.current = res.blobUrl
      setPdfUrl(res.blobUrl)
    }
  }, [revokePdf])

  const handleCreate = useCallback(async () => {
    if (!hasValidLine) {
      toast.error('Add at least one service with an amount.')
      return
    }
    setCreating(true)
    const input = {
      patientName: name.trim(),
      patientMobile: phone.trim(),
      patientEmail: contactContext?.patientEmail || undefined,
      lineItems: lineItems
        .filter((li) => li.description.trim())
        .map((li) => ({
          description: li.description.trim(),
          qty: num(li.qty) || 1,
          amount: num(li.amount),
          discount: num(li.discount),
        })),
      additionalDiscount: num(additionalDiscount),
      payMode,
      amountPaid: num(amountPaid),
      paymentId: paymentId.trim() || undefined,
      remarks: remarks.trim() || undefined,
    }
    const res = await createQuickActionReceipt(contactContext, input)
    setCreating(false)
    if (res?.ok) {
      setResult(res)
      setMode('success')
      loadPdf(res.receiptId)
    } else {
      toast.error(res?.message || 'Unable to create the receipt.')
    }
  }, [
    hasValidLine, name, phone, contactContext, lineItems, additionalDiscount, payMode, amountPaid,
    paymentId, remarks, loadPdf,
  ])

  const handleSend = useCallback(async () => {
    if (!result?.receiptId) return
    setSending(true)
    const res = await sendQuickActionReceipt(result.receiptId, ['whatsapp', 'email'])
    setSending(false)
    if (res?.ok) {
      const channels = []
      if (res.emailSent) channels.push('email')
      if (res.whatsappSent) channels.push('WhatsApp')
      toast.success(channels.length ? `Receipt sent via ${channels.join(' & ')}.` : 'Receipt sent.')
      if (res.whatsappSent || res.emailSent) {
        onSent?.()
      }
    } else {
      toast.error(res?.message || 'Unable to send the receipt.')
    }
  }, [result, onSent])

  const handleDownload = () => {
    if (!pdfUrl) return
    const a = document.createElement('a')
    a.href = pdfUrl
    a.download = `receipt-${result?.receiptNumber || result?.receiptId || 'patient'}.pdf`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }
  const handlePrint = () => {
    if (pdfUrl) window.open(pdfUrl, '_blank', 'noopener,noreferrer')
  }

  const renderEditor = () => (
    <>
      {/* Patient header */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          size="small"
          sx={{ flex: 1 }}
          required
        />
        <TextField
          label="Phone"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          size="small"
          sx={{ flex: 1 }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Status
          </Typography>
          <Chip
            label={status.label}
            size="small"
            sx={{ bgcolor: `${status.color}1a`, color: status.color, fontWeight: 700 }}
          />
        </Box>
      </Stack>

      {/* Services table */}
      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={{ minWidth: 620 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 70px 110px 110px 110px 36px',
              gap: 1,
              px: 1,
              py: 1,
              bgcolor: '#f8fafc',
              borderRadius: '8px',
              fontSize: 12,
              fontWeight: 700,
              color: '#475569',
            }}
          >
            <Box>SERVICE</Box>
            <Box>QTY</Box>
            <Box>AMOUNT</Box>
            <Box>DISCOUNT</Box>
            <Box>TOTAL</Box>
            <Box />
          </Box>
          {lineItems.map((li) => {
            const rowTotal = Math.max(0, num(li.qty) * num(li.amount) - num(li.discount))
            return (
              <Box
                key={li.id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 70px 110px 110px 110px 36px',
                  gap: 1,
                  alignItems: 'center',
                  px: 1,
                  py: 0.75,
                }}
              >
                <Autocomplete
                  freeSolo
                  size="small"
                  options={SERVICE_SUGGESTIONS}
                  inputValue={li.description}
                  onInputChange={(_, value) => updateRow(li.id, 'description', value)}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Start typing to add a service…" variant="standard" />
                  )}
                />
                <TextField
                  type="number"
                  value={li.qty}
                  onChange={(e) => updateRow(li.id, 'qty', e.target.value)}
                  variant="standard"
                  inputProps={{ min: 1, style: { textAlign: 'center' } }}
                />
                <TextField
                  type="number"
                  value={li.amount}
                  onChange={(e) => updateRow(li.id, 'amount', e.target.value)}
                  variant="standard"
                  placeholder="0"
                  InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                  inputProps={{ min: 0 }}
                />
                <TextField
                  type="number"
                  value={li.discount}
                  onChange={(e) => updateRow(li.id, 'discount', e.target.value)}
                  variant="standard"
                  placeholder="0"
                  inputProps={{ min: 0 }}
                />
                <Typography sx={{ fontWeight: 600 }}>{inr(rowTotal)}</Typography>
                <IconButton size="small" onClick={() => removeRow(li.id)} disabled={lineItems.length <= 1}>
                  <DeleteOutlineRoundedIcon fontSize="small" />
                </IconButton>
              </Box>
            )
          })}
        </Box>
      </Box>
      <Button
        onClick={addRow}
        startIcon={<AddRoundedIcon />}
        sx={{ textTransform: 'none', mt: 0.5, color: INDIGO }}
      >
        Add another
      </Button>

      <Divider sx={{ my: 2 }} />

      {/* Footer: inputs + totals */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
        <Stack spacing={1.5} sx={{ flex: 1 }}>
          <TextField
            label="Additional discount"
            type="number"
            size="small"
            value={additionalDiscount}
            onChange={(e) => setAdditionalDiscount(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
          />
          <Stack direction="row" spacing={1.5}>
            <TextField
              label="Pay mode"
              select
              size="small"
              value={payMode}
              onChange={(e) => setPayMode(e.target.value)}
              sx={{ width: 160 }}
            >
              {PAY_MODES.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Amount paid"
              type="number"
              size="small"
              value={amountPaid}
              onChange={(e) => setAmountPaid(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              sx={{ flex: 1 }}
            />
          </Stack>
          <TextField
            label="Payment ID (optional)"
            size="small"
            value={paymentId}
            onChange={(e) => setPaymentId(e.target.value)}
          />
          <TextField
            label="Remarks"
            size="small"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            multiline
            minRows={1}
          />
        </Stack>

        <Box sx={{ width: { xs: '100%', md: 260 }, bgcolor: '#f8fafc', borderRadius: '12px', p: 2 }}>
          <SummaryRow label="Total Amount" value={inr(totals.totalAmount)} />
          <SummaryRow label="Total Discount" value={`- ${inr(totals.totalDiscount)}`} muted />
          <SummaryRow label="Line item discount" value={`- ${inr(totals.lineDiscount)}`} small muted />
          <SummaryRow label="Additional Discount" value={`- ${inr(totals.additionalDiscount)}`} small muted />
          <Divider sx={{ my: 1 }} />
          <SummaryRow label="Grand Total" value={inr(totals.grandTotal)} bold />
          <SummaryRow label="Amount Paid" value={inr(amountPaid)} bold />
        </Box>
      </Stack>
    </>
  )

  const renderSuccess = () => (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
      <Stack spacing={2} sx={{ flex: '0 0 260px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleRoundedIcon sx={{ color: '#16a34a' }} />
          <Typography sx={{ fontWeight: 700 }}>
            Receipt for {inr(result?.grandTotal)} created
          </Typography>
        </Box>
        <ActionRow
          icon={<SendRoundedIcon />}
          title="Send to patient"
          subtitle="Via WhatsApp & email"
          onClick={handleSend}
          busy={sending}
        />
        <ActionRow
          icon={<PrintRoundedIcon />}
          title="Print"
          subtitle="View and print the receipt"
          onClick={handlePrint}
          disabled={!pdfUrl}
        />
        <ActionRow
          icon={<DownloadRoundedIcon />}
          title="Download"
          subtitle="Get a PDF of the receipt"
          onClick={handleDownload}
          disabled={!pdfUrl}
        />
      </Stack>

      <Box
        sx={{
          flex: 1,
          minHeight: 420,
          borderRadius: '12px',
          overflow: 'hidden',
          border: '1px solid #e5e7eb',
          bgcolor: '#f1f5f9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {loadingPdf ? (
          <CircularProgress size={22} />
        ) : pdfUrl ? (
          <iframe title="Receipt preview" src={pdfUrl} style={{ width: '100%', height: 480, border: 'none' }} />
        ) : (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Preview unavailable — use Download.
          </Typography>
        )}
      </Box>
    </Stack>
  )

  return (
    <Dialog open={!!open} onClose={creating ? undefined : onClose} maxWidth="md" fullWidth>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 3, pt: 2.5, pb: 1 }}>
        <ReceiptLongOutlinedIcon sx={{ color: INDIGO }} />
        <Typography sx={{ fontWeight: 700, fontSize: '1.15rem', flex: 1 }}>
          {mode === 'edit' ? 'Create Receipt' : 'Receipt created'}
        </Typography>
        <IconButton onClick={onClose} disabled={creating} aria-label="Close">
          <CloseRoundedIcon />
        </IconButton>
      </Box>
      <Divider />

      <DialogContent>{mode === 'edit' ? renderEditor() : renderSuccess()}</DialogContent>

      <Divider />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, px: 3, py: 2 }}>
        {mode === 'edit' ? (
          <>
            <Button onClick={onClose} disabled={creating} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={creating || !hasValidLine}
              startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <SendRoundedIcon />}
              sx={{ textTransform: 'none', bgcolor: INDIGO, '&:hover': { bgcolor: '#3730a3' } }}
            >
              {creating ? 'Creating…' : 'Create Receipt'}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={resetForm} sx={{ textTransform: 'none' }}>
              New Receipt
            </Button>
            <Button
              variant="contained"
              onClick={onClose}
              sx={{ textTransform: 'none', bgcolor: INDIGO, '&:hover': { bgcolor: '#3730a3' } }}
            >
              Done
            </Button>
          </>
        )}
      </Box>
    </Dialog>
  )
}

function SummaryRow({ label, value, bold, small, muted }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', py: 0.35 }}>
      <Typography
        sx={{ fontSize: small ? 12 : 13, color: muted ? 'text.secondary' : 'text.primary', pl: small ? 1 : 0, fontWeight: bold ? 700 : 400 }}
      >
        {label}
      </Typography>
      <Typography
        sx={{ fontSize: small ? 12 : 13, color: bold ? INDIGO : muted ? 'text.secondary' : 'text.primary', fontWeight: bold ? 800 : 600 }}
      >
        {value}
      </Typography>
    </Box>
  )
}

function ActionRow({ icon, title, subtitle, onClick, busy, disabled }) {
  return (
    <Tooltip title={disabled ? 'Preparing…' : ''}>
      <Box
        component="button"
        onClick={disabled || busy ? undefined : onClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          width: '100%',
          textAlign: 'left',
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          background: '#fff',
          p: 1.5,
          cursor: disabled || busy ? 'default' : 'pointer',
          opacity: disabled ? 0.55 : 1,
          '&:hover': disabled || busy ? {} : { borderColor: INDIGO, background: '#f9fafb' },
        }}
      >
        <Box sx={{ color: INDIGO, display: 'flex' }}>{busy ? <CircularProgress size={20} /> : icon}</Box>
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: 14, lineHeight: 1.2 }}>{title}</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {subtitle}
          </Typography>
        </Box>
      </Box>
    </Tooltip>
  )
}
